import request from "@/http"
import http from "@/http/http"
import type { VisitRecordVO, CompanyPoiCooStatusDTO } from "@/api"
// ==============================================

export interface CompanySceneList {
  // poiId
  poiId: string
  // 名字
  name: string
  // 地址
  address: string
  // 年报地址
  reportAddress: string
  // 注册地址
  registerAddress: string
  // 纬度
  latitude: number
  // 经度
  longitude: number
  // 融资轮次
  financingRounds: string
  // 注册资本(元)
  capitalNum: number
  // 成立时间
  openingDate: string
  // 公司电话
  companyPhone: string
  // 合作状态id
  cooStatusId: string
  // 合作状态
  cooStatusList: CompanyPoiCooStatusDTO[]
}

interface CustomerProgressVO {
  // 横轴 - 季度
  horizontal: string
  // 纵轴 - 进度比例
  vertical: number
}
interface CooperationTypeVO {
  // 业务类型（BusinessCooMode）
  cooMode: string
  // 合作比例
  cooRate: number
}

export interface CompanyCustomerInsightVO {
  // 总客户数
  totalCompany: number
  // 建联客户数
  contactCount: number
  // 合作客户数
  cooperationCount: number
  // 拓客进度
  progress: CustomerProgressVO[]
  // 已合作业务类型
  cooperationType: CooperationTypeVO[]
}

interface CompanySceneArgs {
  /**
   * 城市id
   */
  areaCode: string
  /**
   * 品牌id
   */
  brandId: string
  /**
   * 企业服务类型（CompanyServiceType）
   */
  serviceTypes: string[]
  /**
   * 融资轮次（FinancingRoundsType）
   */
  financingRounds: string[]
  /**
   * 注册资本区间, 单位万元, r1<=r2
   */
  capitalRange: number[][]
  /**
   * 注册时间（格式：2000-01-01）
   */
  capitalDateRange: string[][]
  /**
   * 企业联系方式（CompanyPriorityRecommendType）
   */
  cppRecommend: string
  /**
   * 筛选类型（SceneCustomerQueryType）
   */
  queryType: string
  // queryType == STORE时传参
  /**
   * 半径
   */
  radius: number
  /**
   * 网点id -- poiId
   */
  poiId: string
  // queryType == CITY_RANGE时传参
  /**
   * 区县
   */
  districtId?: string
  /**
   * 街道
   */
  streetId?: string
  /**
   * 网点半径范围
   */
  radiusRange: number
  /**
   * 是否为差集
   */
  isDiff: boolean
  /**
   * 排序类型（SceneSortType）
   */
  sortType: string[]
  /**
   * true-升序，false-降序
   */
  isAsc: boolean[]
  /**
   * 存款合作状态（BusinessCooStatus）
   */
  depositFilter: string[]
  /**
   * 信用卡合作状态（BusinessCooStatus）
   */
  creditCardFilter: string[]
  /**
   * 贷款合作状态（BusinessCooStatus）
   */
  loanFilter: string[]
  /**
   * 商户收单合作状态（BusinessCooStatus）
   */
  businessFilter: string[]
  /**
   * 薪资代发合作状态（BusinessCooStatus）
   */
  salaryFilter: string[]
}

interface CompanySceneListVO {
  // 列表
  list: CompanySceneList[]
  // 企业总数
  companyCount: string
  // 数据总数
  total: number
  // 客户洞察页面
  insight: CompanyCustomerInsightVO
  // 用户请求唯一标识，暂未使用
  uuid: string
}

/**
 * 获取场景拓客—企业列表
 */
export async function getCompanyList(params: CompanySceneArgs): Promise<CompanySceneListVO> {
  try {
    const result = await http.post<CompanySceneListVO>(`/sceneExpand/scene/enterprise`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface ServiceTypeVO {
  type: string
  name: string
}

interface CompanyServiceTypeVO {
  companyType: ServiceTypeVO[]
}

/**
 * 获取服务类型标签
 */
export async function getCompanyServiceType(): Promise<CompanyServiceTypeVO> {
  try {
    const result = await request.get<CompanyServiceTypeVO>("/v1/industry/company/serviceType")
    return result?.data
  } catch (error) {
    throw error
  }
}

interface EnterpriseBasicVO {
  /**
   * 名称
   */
  name: string
  /**
   * 年报地址
   */
  reportAddress: string
  /**
   * 注册地址
   */
  registerAddress: string
  /**
   * 行业
   */
  industry: string
  /**
   * 联系电话
   */
  contactPhone: string
  /**
   * 联系人
   */
  contactUsername: string
  /**
   * 企业维护的id
   */
  enterpriseId: string
}

interface CompanyCooStatusArgs {
  /**
   * 城市id
   */
  areaCode: string
  /**
   * 品牌id
   */
  brandId: string
  /**
   * poiId
   */
  poiId: string
  /**
   * 合作明细
   */
  cooDetails: CompanyPoiCooStatusDTO[]
  /**
   * 企业基本信息
   */
  enterpriseBasic: EnterpriseBasicVO
  /**
   * 拜访记录
   */
  remark: string
}

/**
 * 更改公司场景合作状态
 * @returns 主键id
 */
export async function changeCompanyCooStatus(params: CompanyCooStatusArgs): Promise<string> {
  try {
    const result = await http.post<string>(`/sceneExpand/scene/enterprise/maintain`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

// interface HourBasicItem {
//   /**
//    * 小时
//    */
//   hour: string
//   /**
//    * 指数 占比，保留四位小数
//    */
//   count: number
// }
// interface ProfileBasicItem {
//   /**
//    * 标签id
//    */
//   id: string
//   /**
//    * 标签名字
//    */
//   name: string
//   /**
//    * 占比，保留四位小数
//    */
//   ratio: number
//   /**
//    * 经度
//    */
//   longitude: number
//   /**
//    * 纬度
//    */
//   latitude: number
// }

const enum ComStatus {
  ACTIVE = "存续",
  UNREGISTERED = "注销",
  DISSOLVED = "撤销",
  REVOKED = "吊销",
}

type CompanySummary = {
  // 品牌类型
  brandType: string
  // 上市板块
  listingPlate: string[]
  // 法定代表人
  legalPerson: string
  // 经营状态
  operationStatus: ComStatus
  // 注册资本
  registryCapital: number
  // 实缴资本
  payCapital: number
  // 统一社会信用代码
  unifiedSocialCreditCode: string
  // 组织机构代码
  organizationCode: string
  // 企业邮箱
  enterpriseMail: string
  // 企业电话
  enterprisePhone: string
  // 注册地址
  registryAddress: string
  // 经营地址
  operationAddress: string
  // 年报地址
  annualReportAddress: string
  // 成立时间（注册时间）
  registryDate: string
}

type AnalysisData = {
  // 值
  value: number
  // 年度
  year: string
}

export type FinancingData = {
  // 值
  value: number
  // 年度
  year: string
  // 轮数
  financingRoundsType: string
  // 单位
  unit: string
}

type FinancialAnalysis = {
  // 净利润
  netProfits: AnalysisData[]
  // 利润总和
  totalProfits: AnalysisData[]
  // 销售总额
  totalSales: AnalysisData[]
  // 主营业务收入
  mainBusinessIncome: AnalysisData[]
  // 资产总额
  totalAssets: AnalysisData[]
  // 纳税总额
  totalTaxes: AnalysisData[]
  // 负债总额
  totalDebt: AnalysisData[]
  // 实际员工数
  actualEmployees: AnalysisData[]
}

type PaymentSituation = {
  // 生育保险本期实际缴费/欠缴金额
  birthAmount: string
  // 失业保险本期实际缴费/欠缴金额
  unemploymentAmount: string
  // 职工基本医疗保险本期实际缴费/欠缴金额
  medicalAmount: string
  // 工伤保险本期实际缴费/欠缴金额
  industrialAccidentAmount: string
  // 城镇职工基本养老保险本期实际缴费/欠缴金额
  endowmentAmount: string
}

type SocialSecurityData = {
  // 实缴金额
  paidAmount: PaymentSituation
  // 欠缴金额
  arrearsAmount: PaymentSituation
}

type DevelopDetail = {
  // 排名
  rank: number
  // 值
  value: number
  // d单位
  unit: string
  // 指标
  index: number
}

type DevelopAbility = {
  // 平均月工资
  monthlySalary: DevelopDetail
  // 商标数
  trademarkNumber: DevelopDetail
  // 专利数
  patentsNumber: DevelopDetail
  // 著作数
  worksNumber: DevelopDetail
  // 资质认证数
  certificatesNumber: DevelopDetail
}

type BidInfo = {
  // 项目名称
  projectName: string
  // 中标单位
  winCompany: string
  // 中标时间
  winDate: string
  // 预算控制价
  budgetPrice: number
}

type CompetitionAbility = {
  // 研发实力
  developAbility: DevelopAbility
  // 中标信息
  winBid: BidInfo[]
  // 招标信息
  bidding: BidInfo[]
}

type RiskSituation = {
  // 风险金额
  amount: number
  unit: string
  // 涉及人数/次数
  count: number
  // 风险时间
  date: string
}

type LiquidityRisk = {
  // 股权质押
  equityPledge: RiskSituation
  // 股权冻结
  equityFreeze: RiskSituation
  // 动产抵押
  chattelMortgage: RiskSituation
}

type LegalRisk = {
  // 工商抽查
  industryCommercialCheck: RiskSituation
  // 企业失信
  enterpriseDishonesty: RiskSituation
  // 被限制高消费
  restrictedHighConsume: RiskSituation
  // 行政处罚
  administrativeSanction: RiskSituation
}

interface PointProfile {
  // 名字
  // name: string
  // // 数据统计时间
  // dataDate: string
  // // 开业时间
  // openingDate: string
  // // 地址
  // address: string
  // // 纬度
  // latitude: number
  // // 经度
  // longitude: number
  // // 上月客流（总客流）
  // lastMonthFlowCount: number
  // // 上月客流（常驻客流）
  // residentFlowCount: number
  // // 上月月度平均分时指数
  // hourRatio: HourBasicItem[]
  // // 年龄分布
  // age: ProfileBasicItem[]
  // // 消费能力
  // consumption: ProfileBasicItem[]
  // // 房产级别
  // housePrice: ProfileBasicItem[]
  // // 资产情况
  // carAssets: ProfileBasicItem[]
  // // 生态类型（中文）-- 大数据返回的类型
  // ecologyType: string
  // // 生态信息
  // ecologyInfo: any
  // // aoi围栏
  // aoiPolygon: number[][]
  // // 底层库里的联系公司电话
  // companyPhone: string

  companySummary: CompanySummary
  // 财务分析
  financialAnalysis: FinancialAnalysis
  // 融资历程
  financingProcess: FinancingData[]
  // 社保数据
  socialSecurityData: SocialSecurityData

  // 竞争力分析
  competitionAbility: CompetitionAbility

  // 流动性风险
  liquidityRisk: LiquidityRisk

  // 法律风险
  legalRisk: LegalRisk

  // 违约风险
  defaultRisk: { riskLevel: string }
}

export type CompanyMaintainedInfoDTO = EnterpriseBasicVO & {
  /**
   * 与企业沟通信息
   */
  exchangeInfo: string
  /**
   * 维护的合作信息
   */
  poiCooperation: CompanyPoiCooStatusDTO[]
}

interface ScenePointProfileArgs {
  // 城市id
  areaCode: string
  // 品牌id
  brandId: string
  // poi id
  poiId: string
  // tag
  profileTag: string
}

export interface CompanyPointProfileVO {
  // poi名称
  poiName: string
  // 数据时间
  dataDate: string
  // 企业画像
  populationAnalysis: PointProfile
  // 维护信息
  maintainInfo: CompanyMaintainedInfoDTO
  // 拜访记录
  visitRecords: VisitRecordVO[]
}

/**
 * 获取指定地点的poi客流画像
 */
export async function getCompanyProfile(
  params: ScenePointProfileArgs,
): Promise<CompanyPointProfileVO> {
  try {
    const { profileTag, ...others } = params
    const result = await http.post<CompanyPointProfileVO>(
      `/sceneExpand/scene/enterprise/profile/${profileTag}`,
      others,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}
